import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorage } from 'angular-web-storage';

import { ListResponse } from '../../../../models/list-response';
import { AccountService } from '../../../../services/account/account.service';
import { addFiltersToGetParams } from '../../../../utils';
import { Association, AssociationFilters, loadAssociationFiltersFromGetParams } from '../../../../models/association';
import { AssociationService } from '../../../../services/association/association.service';
import { CreateNewAssociationModalComponent } from './modals/create-new-association-modal/create-new-association-modal.component';

@Component({
  selector: 'app-associations',
  templateUrl: './associations.component.html',
  styleUrls: ['./associations.component.css']
})
export class AssociationsComponent implements OnInit {
  public pageNumber = 1;
  @LocalStorage() public pageSize;
  public filters: AssociationFilters = {
    search: ''
  };

  public filtersChangedSubject = new Subject<null>();

  public associationList: ListResponse<Association>;

  public loadingAssociations = true;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private associationService: AssociationService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    // Make browser history available to the template.
    this.history = history;

    // Handle changes to the filters by reloading the associations,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => { this.loadingAssociations = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadAssociations();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = loadAssociationFiltersFromGetParams(this.activatedRoute);

    this.loadAssociations();
  }

  public loadAssociations() {
    this.loadingAssociations = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.associationService
      .getAssociations(this.pageNumber, this.pageSize, this.filters)
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.associationList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingAssociations = false;
        }
      });
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.associationList &&
      this.associationList.results &&
      (this.associationList.results.length > 0)
    );
  }

  public handleCreateNewAssociation() {
    const modalRef = this.modalService.open(CreateNewAssociationModalComponent);
    modalRef.componentInstance.savedCallback = (createdAssociation: Association) => {
      this.router.navigate(['associations', createdAssociation.id.toString()]);
    }
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadAssociations();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.associationList) { return 1; }
    return Math.ceil(this.associationList.count / this.pageSize);
  }
}
